<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'partnerSubTypeList' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <PartnerSubTypeForm
      v-if="partnerSubType || isNew"
      :partner-sub-type="partnerSubType"
      :is-loading="isLoading"
      :is-fetching="isFetching"
      @on-submit="onSubmit"
    />
    <div v-else class="flex justify-center"><Spinner /></div>
  </section>
</template>

<script lang="ts" setup>
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import Spinner from '@/components/icons/Spinner.vue';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import router from '@/router';
import {
  useCreatePartnerSubType,
  usePartnerSubType,
  useUpdatePartnerSubType,
} from '@/queries/use-users';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { CreatePartnerSubTypeDto, UpdatePartnerSubTypeDto } from '@carvia/ros-client-types';
import PartnerSubTypeForm from './PartnerSubTypeForm.vue';

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const uiStore = useUiStore();
const route = useRoute();

const partnerSubTypeId = ref(route.params.id as string);
const isNew = computed(() => partnerSubTypeId.value === 'new');

const { data: partnerSubType, isFetching } = usePartnerSubType(
  partnerSubTypeId,
  computed(() => !isNew.value),
);
const { mutateAsync: createPartnerSubType, isPending: isCreating } = useCreatePartnerSubType();
const { mutateAsync: updatePartnerSubType, isPending: isUpdating } = useUpdatePartnerSubType();

const isLoading = computed(() => isCreating.value || isUpdating.value);

watchEffect(() => {
  if (partnerSubType.value) {
    uiStore.setHeaderTitle(t('partnerSubType'), getLocalizedField(partnerSubType.value.name));
  } else {
    uiStore.setHeaderTitle(t('partnerSubType'));
  }
});

const onSubmit = async (values: UpdatePartnerSubTypeDto) => {
  if (isNew.value) {
    const created = await createPartnerSubType(values as CreatePartnerSubTypeDto);
    await router.replace({ name: 'partnerSubType', params: { id: created.id } });
    partnerSubTypeId.value = created.id;
    return;
  }

  await updatePartnerSubType({
    id: partnerSubTypeId.value,
    subType: values,
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "partnerSubType": "Partner Sub Type"
  },
  "de": {
    "partnerSubType": "Partner-Subtyp"
  }
}
</i18n>
